import moment from 'moment';
import { formatShortDate } from './BillDetails/billDetailsUtils';
import { DASH, getBillUtilityIcons, getUtilityLabel } from './companyBillRows';
const DAY = 'YYYY-MM-DD';

/** A date the service sent, to the day; anything else reads as no date. */
const toDay = value => {
  if (typeof value !== 'string') return null;
  const day = moment(value.slice(0, 10), DAY, true);
  return day.isValid() ? day.format(DAY) : null;
};
const counted = (count, one, many) => "".concat(count, " ").concat(count === 1 ? one : many);
const ordinal = day => {
  const lastTwo = day % 100;
  if (lastTwo >= 11 && lastTwo <= 13) return "".concat(day, "th");
  return "".concat(day).concat({
    1: 'st',
    2: 'nd',
    3: 'rd'
  }[day % 10] || 'th');
};
const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1);

/** Names the property as well: one utility billed on two properties is two questions, with two answers. */
export const billReadyKey = (billId, propertyId, utilityId) => "".concat(billId, "|").concat(propertyId, "|").concat(utilityId);

/**
 * What the Bills page asks property-service (LB-6032): each utility a row's bill charges a property for, with the
 * bill's period end. A row with no period end, and a service not yet matched to a property utility, are left out.
 */
export const toBillReadyQueries = rows => (rows !== null && rows !== void 0 ? rows : []).flatMap(row => {
  var _row$utilities;
  const serviceEnd = toDay(row.serviceEndDate);
  if (!serviceEnd) return [];
  const keys = new Set();
  return ((_row$utilities = row.utilities) !== null && _row$utilities !== void 0 ? _row$utilities : []).flatMap(utility => {
    if (!(utility !== null && utility !== void 0 && utility.propertyId) || !(utility !== null && utility !== void 0 && utility.utilityId)) return [];
    const key = billReadyKey(row.id, utility.propertyId, utility.utilityId);
    if (keys.has(key)) return [];
    keys.add(key);
    return [{
      key,
      propertyId: utility.propertyId,
      utilityId: utility.utilityId,
      serviceEnd
    }];
  });
});
const reasonFor = answer => {
  switch (answer.lagBasis) {
    case 'utility':
      {
        // property-service drops a bill dated before its period end when it learns the lag, so it is never negative.
        const when = answer.lagDays === 0 ? 'on the day the period ends' : "".concat(counted(answer.lagDays, 'day', 'days'), " after the period ends");
        return "usually ".concat(when, ", from ").concat(counted(answer.lagSampleCount, 'bill', 'bills'));
      }
    case 'provider':
      return "estimated from the service provider's other bills";
    case 'override':
      return "set to the ".concat(ordinal(moment(answer.readyDate, DAY).date()), " of the month");
    default:
      // A bill's lag is learned only from bills that ended before it, so an old bill can have none even when its
      // utility has plenty of later ones. "Yet" would read as if the utility had no history at all.
      return 'no earlier bill dates for this utility';
  }
};

/** The utility a question was about, as the utilities column names it. */
const utilityName = (row, query) => {
  var _row$utilities2;
  const utility = ((_row$utilities2 = row.utilities) !== null && _row$utilities2 !== void 0 ? _row$utilities2 : []).find(candidate => (candidate === null || candidate === void 0 ? void 0 : candidate.propertyId) === query.propertyId && (candidate === null || candidate === void 0 ? void 0 : candidate.utilityId) === query.utilityId);
  return getBillUtilityIcons([utility]).map(getUtilityLabel).join('/');
};

/**
 * One row's Bill ready cell: the earliest ready date across its utilities, and a tooltip that leads with the date
 * printed on the bill and says where each ready date came from. A utility nothing was learned for has only its
 * period end as an answer, which tells the reader nothing, so it shows no date of its own.
 */
const isDated = answer => answer.lagBasis !== 'none' && !!toDay(answer.readyDate);
const cellFor = (row, answersByKey) => {
  const printedDay = toDay(row.invoiceDate);
  const printed = printedDay ? "Billed ".concat(moment(printedDay, DAY).format('MMM D, YYYY')) : null;
  const answered = toBillReadyQueries([row]).map(query => ({
    query,
    answer: answersByKey.get(query.key)
  })).filter(_ref => {
    let answer = _ref.answer;
    return answer;
  });
  const dated = answered.filter(_ref2 => {
    let answer = _ref2.answer;
    return isDated(answer);
  });
  const reasons = answered.length === 1 ? [reasonFor(answered[0].answer)] : answered.map(_ref3 => {
    let query = _ref3.query,
      answer = _ref3.answer;
    const date = isDated(answer) ? "".concat(moment(answer.readyDate, DAY).format('MMM D'), ", ") : '';
    return "".concat(utilityName(row, query), ": ").concat(date).concat(reasonFor(answer));
  });
  const title = answered.length === 1 && printed ? "".concat(printed, " \xB7 ").concat(reasons[0]) : [printed, ...reasons.map(capitalized)].filter(Boolean).join('\n');
  if (!dated.length) return {
    readyDate: null,
    label: DASH,
    title
  };
  const earliest = dated.map(_ref4 => {
    let answer = _ref4.answer;
    return toDay(answer.readyDate);
  }).sort()[0];
  return {
    readyDate: earliest,
    label: formatShortDate(earliest),
    title
  };
};

/**
 * The Bill ready cell for every row (LB-6032), from property-service's answers. Rows with no answer yet, or none at
 * all, get no date: the column shows a dash, never a guess.
 */
export const toBillReadyCells = (rows, answers) => {
  const answersByKey = new Map((answers !== null && answers !== void 0 ? answers : []).map(answer => [answer.key, answer]));
  return Object.fromEntries((rows !== null && rows !== void 0 ? rows : []).map(row => [row.id, cellFor(row, answersByKey)]));
};